import { CATALOGUE_MODE, TCPOS } from "@constants";
import { io, Socket } from "socket.io-client";
import { v4 as uuid } from "uuid";
import { snapshot, useSnapshot } from "valtio";

import React from "react";
import { useDispatch } from "react-redux";

import { setIsAppPaused, setIsLicenseProblem, store } from "@store";

import { getCatalogueCarte } from "@helpers/fetcher";
import { connectWebSocket, handleLedControl } from "@helpers/general";

import { checkforLicenseAvailability } from "@components/OrderTaking/Helpers";
import {
    setCategoriesVisibility,
    setItemVisibility,
    setOutOfStockItems,
} from "@components/OrderTaking/Store/feature";
import {
    setSelectedShopApp,
    syncStore,
} from "@components/Synchronization/store";

import {
    setConnectedLedPort,
    setIsLedActive,
    setIsProjectMustBeUpdated,
} from "@pages/Kiosk/store";
import {
    setIsAppMustBeResynchronise,
    setIsKioskMustUpdate,
} from "@pages/Kiosk/store/actions";

export function useSocketConnection(): void {
    const dispatch = useDispatch();
    const { middlewareApiUrl, posEditor } = useSnapshot(store);
    const { machineIpAddress } = useSnapshot(syncStore);
    // eslint-disable-next-line
    const shopDetail: any = JSON.parse(localStorage.getItem("shopDetail")!);
    const oldUuidSession = localStorage.getItem("uuidSession");
    let machineIdentifier = localStorage.getItem("machineIdentifier");
    // eslint-disable-next-line
    const ledConfig = JSON.parse(localStorage.getItem("ledConfig") as any);

    const socketRef = React.useRef<Socket | null>(null);

    React.useEffect(() => {
        const socketApi = middlewareApiUrl.split("/api")[0];
        const { selectedShopApp } = snapshot(syncStore);
        console.log("🔌 Connecting to socket at:", socketApi);
        // Disconnect old socket if exists
        if (socketRef.current) {
            localStorage.removeItem("uuidSession");
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        if (!machineIdentifier) {
            machineIdentifier = uuid();
            localStorage.setItem("machineIdentifier", machineIdentifier);
            console.log(
                `Generated new machineIdentifier: ${machineIdentifier}`
            );
        }

        const socket = io(socketApi, {
            query: {
                uuidConnectedEntity: shopDetail?.franchise_uid,
                shopUid: shopDetail?.shop_uid,
                IPaddress: machineIpAddress,
                kioskName: selectedShopApp.name,
                machineIdentifier: machineIdentifier,
            },
            forceNew: false,
            transports: ["polling", "websocket"],
            reconnection: true,
            reconnectionDelay: 10000, // defaults to 1000
            reconnectionDelayMax: 10000, // defaults to 5000

            reconnectionAttempts: Infinity,
            timeout: 30000, // Adjust connection timeout as needed
        });

        socketRef.current = socket;
        const username = `${shopDetail?.schema}|${shopDetail?.id}`;

        socket.on("connect", () => {
            const uidModification: ModificationUuidType = JSON.parse(
                localStorage.getItem("uidModification") as string
            );
            console.log(`emit check modification event`);
            //! here verification of modification on first connection
            socket.emit(
                "checkModification",
                {
                    username,
                    message: uidModification,
                },
                (response: string) => {
                    console.log(
                        `returned message from check modification: ${response}`
                    );
                }
            );
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.on(
            "openSession",
            // eslint-disable-next-line
            ({ uuidSession, hostname }: any) => {
                localStorage.setItem("serverHostName", hostname || "");
                if (
                    (uuidSession === 0 || oldUuidSession === undefined) &&
                    process.env.REACT_APP_MODE !== CATALOGUE_MODE
                ) {
                    setIsLicenseProblem(true);
                    localStorage.removeItem("uuidSession");
                    console.log(
                        `license problem detected on connection with uuidSession: ${uuidSession}`
                    );
                    return;
                }
                setIsLicenseProblem(false);
                console.log(
                    `Successfully open license session on connection with uuidSession: ${uuidSession}`
                );
                localStorage.setItem("uuidSession", uuidSession);
            }
        );

        socket.on("Standby_Kiosk", ({ message, kioskName, kioskState }) => {
            const uidModification: ModificationUuidType = JSON.parse(
                localStorage.getItem("uidModification") as string
            );
            console.log(`Kioask application Paused event from Redis`);
            if (uidModification?.standByKiosk !== message) {
                localStorage.setItem(
                    "uidModification",
                    JSON.stringify({
                        ...uidModification,
                        standByKiosk: message,
                    })
                );

                if (
                    uidModification?.standByKiosk !== "" &&
                    (kioskName === selectedShopApp.name || kioskName === "all")
                ) {
                    console.log(
                        `Kioask application Paused event with "isAppPaused": ${kioskState}`
                    );
                    setIsAppPaused(kioskState);
                    if (ledConfig.isLedActive) {
                        if (kioskState) {
                            handleLedControl(
                                ledConfig.connectedLedPort,
                                "Orange"
                            );
                        } else {
                            handleLedControl(
                                ledConfig.connectedLedPort,
                                "Green"
                            );
                        }
                    }
                }
            }
        });

        socket.on(
            "ledAlert",
            ({ clientIpAdress, ledPort, isVisibiliteLedActive }) => {
                if (
                    clientIpAdress !== machineIpAddress ||
                    process.env.REACT_APP_MODE === CATALOGUE_MODE
                ) {
                    return;
                }

                console.log(
                    `Led configuration has been setted with typeLed : ${ledPort} and led visibility is ${isVisibiliteLedActive}`
                );
                if (isVisibiliteLedActive === false && ledConfig.isLedActive) {
                    handleLedControl(ledConfig.connectedLedPort, "Disable");
                    setIsLedActive(isVisibiliteLedActive as boolean);
                    setConnectedLedPort(ledPort as string);
                } else {
                    setIsLedActive(isVisibiliteLedActive as boolean);
                    setConnectedLedPort(ledPort as string);
                }
            }
        );

        socket.on("New_catalog", ({ message }: { message: string }) => {
            console.log(`New Card event detected with ID: ${socket.id}`);
            const uidModification: ModificationUuidType = JSON.parse(
                localStorage.getItem("uidModification") as string
            );

            if (uidModification?.catalog !== message) {
                localStorage.setItem(
                    "uidModification",
                    JSON.stringify({
                        ...uidModification,
                        catalog: message,
                    })
                );

                getCatalogueCarte(dispatch, shopDetail, middlewareApiUrl);
            }
        });

        socket.on(
            "New_setting",
            ({ message }: { message: Record<string, string> }) => {
                console.log(`New Setting event detected with ID: ${socket.id}`);
                const modificationUuid: ModificationUuidType = JSON.parse(
                    localStorage.getItem("uidModification") as string
                );

                localStorage.setItem(
                    "uidModification",
                    JSON.stringify({
                        ...modificationUuid,
                        setting: message,
                    })
                );

                if (
                    modificationUuid.setting !==
                    (message as Record<string, string> | string)
                ) {
                    console.log(
                        `Setting: setIsProjectMustBeUpdated true to  be updated in standby screen page`
                    );
                    setIsProjectMustBeUpdated({
                        setting: true,
                        peripheral: false,
                    });
                }
            }
        );

        socket.on("New_peripheral", ({ message }: { message: string }) => {
            console.log(
                `New Peripheral event detected with ID: ${socket.id}, Message: ${message}`
            );
            const modificationUuid: ModificationUuidType = JSON.parse(
                localStorage.getItem("uidModification") as string
            );

            localStorage.setItem(
                "uidModification",
                JSON.stringify({
                    ...modificationUuid,
                    peripheral: message,
                })
            );

            if (modificationUuid.peripheral !== message) {
                console.log(
                    `Peripheral: setIsProjectMustBeUpdated true to  be updated in standby screen page`
                );
                setIsProjectMustBeUpdated({
                    setting: false,
                    peripheral: true,
                });
            }
        });
        socket.on(
            "resynchronise",
            ({ message, appId }: { message: string; appId: string }) => {
                console.log(
                    `Resynchronise Code event detected with ID: ${socket.id}, Message: ${message}`
                );
                const modificationUuid: ModificationUuidType = JSON.parse(
                    localStorage.getItem("uidModification") as string
                );

                localStorage.setItem(
                    "uidModification",
                    JSON.stringify({
                        ...modificationUuid,
                        appId: message,
                    })
                );
                if (selectedShopApp.appId === appId) {
                    setIsAppMustBeResynchronise(true);
                }
            }
        );
        socket.on(
            "pass_code",
            ({ message, passcode }: { message: string; passcode: string }) => {
                console.log(
                    `New Pass Code event detected with ID: ${socket.id}, Message: ${message}`
                );
                const modificationUuid: ModificationUuidType = JSON.parse(
                    localStorage.getItem("uidModification") as string
                );

                localStorage.setItem(
                    "uidModification",
                    JSON.stringify({
                        ...modificationUuid,
                        passCode: message,
                    })
                );

                if (modificationUuid.peripheral !== message) {
                    setSelectedShopApp({
                        ...selectedShopApp,
                        passcode,
                    });
                }
            }
        );

        socket.on("New_certificate", ({ message }: { message: string }) => {
            console.log(`New certificate event detected with ID: ${socket.id}`);
            const uidModification: ModificationUuidType = JSON.parse(
                localStorage.getItem("uidModification") as string
            );
            if (uidModification?.console === message) return;
            localStorage.setItem(
                "uidModification",
                JSON.stringify({
                    ...uidModification,
                    console: message,
                })
            );
            checkforLicenseAvailability();
        });

        socket.on("disconnect", (reason) => {
            if (reason === "io server disconnect") {
                console.log(
                    `socket disconnect due  server disconnect with reason: ${JSON.stringify(
                        reason
                    )}`
                );
                // disconnect initiated by server. Manually reconnect
                socket.connect();
            } else {
                console.log(
                    `License session closed due socket disconnection with reason: ${JSON.stringify(
                        reason
                    )}`
                );
                localStorage.removeItem("uuidSession");
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.on("connect_error", (err: any) => {
            if (err.type === "TransportError") {
                // the Socket will automatically try to reconnect
            } else {
                socket.connect();
            }
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.on("out_of_stock", ({ message }: { message: any }) => {
            console.log(
                `New out of stock event detected with data message: ${JSON.stringify(
                    message
                )}`
            );
            dispatch(setOutOfStockItems(message));
        });

        socket.on("update_kiosk", () => {
            console.log(
                `New update kiosk event detected with ID: ${socket.id}`
            );
            setIsKioskMustUpdate(true);
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.on(
            "update_visibility",
            ({
                message,
                globalCategory,
            }: {
                message: any;
                globalCategory: any;
            }) => {
                console.log(
                    `New item visibility event detected with data message: ${JSON.stringify(
                        message
                    )}`
                );

                switch (message.visibilityType) {
                    case "item":
                        dispatch(setItemVisibility(message.itemsVisibilities));
                        break;
                    case "category":
                        dispatch(
                            setCategoriesVisibility({
                                categoriesVisibilities:
                                    message.categoriesVisibilities,
                                globalCategory,
                            })
                        );
                        break;
                }
            }
        );

        if (posEditor === TCPOS) {
            connectWebSocket();
        }

        return () => {
            localStorage.removeItem("uuidSession");
            console.log("🔌 Disconnecting from socket");
            if (socketRef.current) {
                socketRef.current.off("New_catalog");
                socketRef.current.off("New_setting");
                socketRef.current.off("openSession");
                socketRef.current.off("New_certificate");

                socketRef.current.off("Standby_Kiosk");
                socketRef.current.off("ledAlert");
                socketRef.current.off("out_of_stock");
                socketRef.current.off("update_kiosk");
                socketRef.current.off("update_visibility");
                socketRef.current.off("resynchronise");
                socketRef.current.off("pass_code");
                socketRef.current.off("New_peripheral");
                socketRef.current.off("disconnect");
                socketRef.current.off("connect_error");
                socketRef.current.disconnect();
            }
        };
    }, [middlewareApiUrl, machineIpAddress]);
}
